import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, Lock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeadCapture = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    businessName: "",
    websiteUrl: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        business_name: form.businessName,
        website_url: form.websiteUrl,
      });

      localStorage.setItem(
        "coc_lead",
        JSON.stringify({ ...form, lead_id: res.data.lead_id })
      );

      navigate("/try");
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-white border-2 border-[#E9ECEF] text-[#2D3436] placeholder-[#A0AEC0] rounded-2xl px-5 py-4 focus:outline-none focus:border-[#FF6B6B] focus:ring-4 focus:ring-[#FF6B6B]/10 transition-all text-base";

  return (
    <div className="min-h-screen bg-[#FFFDF9] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md animate-fade-in-up">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-[#FFE3E3] text-[#FF6B6B] px-4 py-2 rounded-full text-sm font-semibold mb-5">
            <Sparkles size={14} />
            Free 2-Week Trial
          </div>
          <h1
            style={{ fontFamily: "'Young Serif', Georgia, serif" }}
            className="text-5xl font-bold text-[#2D3436] tracking-tight leading-tight mb-3"
          >
            Get Your Free<br />Content Prompts
          </h1>
          <p className="text-base text-[#636E72] leading-relaxed">
            We'll create personalized SMS prompts just for your business —
            completely free, no credit card required.
          </p>
        </div>

        {/* Card */}
        <div
          data-testid="lead-capture-form"
          className="bg-white rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.06)] p-7 border border-[#F1F3F5]"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#2D3436] mb-1.5">
                Full Name
              </label>
              <input
                data-testid="lead-form-name-input"
                type="text"
                value={form.name}
                onChange={handleChange("name")}
                placeholder="Jane Smith"
                required
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2D3436] mb-1.5">
                Email
              </label>
              <input
                data-testid="lead-form-email-input"
                type="email"
                value={form.email}
                onChange={handleChange("email")}
                placeholder="jane@mybusiness.com"
                required
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2D3436] mb-1.5">
                Phone Number
              </label>
              <input
                data-testid="lead-form-phone-input"
                type="tel"
                value={form.phone}
                onChange={handleChange("phone")}
                placeholder="+1 (555) 000-0000"
                required
                className={inputClass}
              />
              <div className="flex items-center gap-1.5 mt-1.5">
                <Lock size={11} className="text-[#636E72]" />
                <span className="text-xs text-[#636E72]">
                  We'll text your personalized prompts here. No spam, ever.
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#2D3436] mb-1.5">
                Business Name
              </label>
              <input
                data-testid="lead-form-business-input"
                type="text"
                value={form.businessName}
                onChange={handleChange("businessName")}
                placeholder="My Awesome Business"
                required
                className={inputClass}
              />
            </div>

{error && (
              <div
                data-testid="lead-form-error"
                className="text-[#FF6B6B] text-sm bg-[#FFE3E3] rounded-2xl p-3"
              >
                {error}
              </div>
            )}

            <button
              data-testid="lead-form-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF6B6B] text-white font-bold rounded-full px-8 py-4 mt-2 shadow-[0_8px_20px_rgba(255,107,107,0.3)] hover:shadow-[0_12px_25px_rgba(255,107,107,0.4)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
            >
              {loading ? (
                "Starting your trial..."
              ) : (
                <>
                  Get My Free Prompts <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Trust signals */}
        <div className="flex justify-center items-center gap-5 mt-5 text-sm text-[#636E72] flex-wrap">
          {["100% Free", "No credit card", "Opt out anytime"].map((t) => (
            <span key={t} className="flex items-center gap-1.5">
              <span className="text-[#FF6B6B] font-bold">✓</span> {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeadCapture;
